import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ReactSortable } from 'react-sortablejs'
import { FaEye, FaEyeSlash, FaGripVertical, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa'
import toast from 'react-hot-toast'
import config from '@/constant/config'
import { userGrantsSelector } from '@/store/slices/user'
import {
  deleteHighlightSection,
  getHighlightSections,
  updateHighlightSectionSequence
} from '@/store/slices/highlight-section'

import FullPageLoader from '@/components/FullPageLoader'
import Header from '@/components/Header'

const themeClasses = {
  MULTI_YELLOW: 'bg-[#fff4b8] border-[#212121]',
  ELECTRIC_BLUE: 'bg-[#dfeeff] border-[#183153]',
  CORAL_POP: 'bg-[#ffe2dc] border-[#7a263a]'
}

const normalizeCustomThemeColor = value => {
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || '')) return null
  const color = value.toUpperCase()
  return color.length === 4
    ? `#${color.slice(1).split('').map(character => character + character).join('')}`
    : color
}

const mixColors = (base, target, amount) => {
  const channels = [1, 3, 5].map(index => Math.round(
    parseInt(base.slice(index, index + 2), 16) * (1 - amount) + parseInt(target.slice(index, index + 2), 16) * amount
  ).toString(16).padStart(2, '0'))
  return `#${channels.join('')}`.toUpperCase()
}

const getSectionTheme = section => {
  const customThemeColor = section.theme === 'CUSTOM' && normalizeCustomThemeColor(section.customThemeColor)
  if (!customThemeColor) {
    return { className: themeClasses[section.theme] || themeClasses.MULTI_YELLOW }
  }

  return {
    className: '',
    style: {
      backgroundColor: customThemeColor,
      borderColor: mixColors(customThemeColor, '#000000', 0.56)
    }
  }
}

const getSectionDisplayTitle = section => section.title || section.highlightTitle || 'Untitled Highlight section'

const HighlightSectionPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const grants = useSelector(userGrantsSelector)
  const permissions = grants.homepage_highlight || []

  const [sections, setSections] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchSections = async () => {
    try {
      setIsLoading(true)
      const { data } = await dispatch(getHighlightSections()).unwrap()
      setSections(data)
    } catch(err) {
      toast.error('Unable to load Highlight sections')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSections()
  }, [])

  const moveSection = async e => {
    if (e.oldIndex === e.newIndex) return
    try {
      setIsLoading(true)
      const id = e.item.dataset.sectionId
      const { data } = await dispatch(updateHighlightSectionSequence({
        id,
        sequence: e.newIndex + 1
      })).unwrap()
      setSections(data)
    } catch(err) {
      toast.error('Unable to reorder Highlight sections')
      await fetchSections()
    } finally {
      setIsLoading(false)
    }
  }

  const removeSection = async section => {
    const displayTitle = getSectionDisplayTitle(section)
    if (!window.confirm(`Delete “${displayTitle}” and its uploaded images?`)) return
    try {
      setIsLoading(true)
      await dispatch(deleteHighlightSection(section.id)).unwrap()
      toast.success('Highlight section deleted')
      await fetchSections()
    } catch(err) {
      toast.error('Unable to delete Highlight section')
      setIsLoading(false)
    }
  }

  return (
    <Header
      title="Highlight Sections"
      slot={() => permissions.includes('create') && (
        <button
          type="button"
          className="ml-auto flex items-center gap-2 rounded-full bg-gray-800 px-5 py-2 text-sm font-semibold text-white"
          onClick={() => navigate(config.path.content.highlightSectionCreate)}
        >
          <FaPlus /> Create section
        </button>
      )}
    >
      {isLoading && <FullPageLoader />}

      {!isLoading && !sections.length && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-8 py-16 text-center">
          <div className="text-xl font-bold">No Highlight sections yet</div>
          <div className="mt-2 text-sm text-gray-500">Create a visual path into categories, brands, series, or characters.</div>
        </div>
      )}

      {!!sections.length && (
        <ReactSortable
          className="grid gap-5"
          list={sections}
          setList={setSections}
          onEnd={moveSection}
          disabled={!permissions.includes('update')}
          handle=".highlight-drag-handle"
        >
          {sections.map(section => {
            const sectionTheme = getSectionTheme(section)
            return (
              <article
                key={section.id}
                data-section-id={section.id}
                className={`overflow-hidden rounded-2xl border-2 shadow-sm ${sectionTheme.className}`}
                style={sectionTheme.style}
              >
              <div className="flex items-center gap-4 p-5">
                <button
                  type="button"
                  aria-label="Drag to reorder"
                  className="highlight-drag-handle cursor-move text-gray-500"
                >
                  <FaGripVertical />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-xl font-bold">{getSectionDisplayTitle(section)}</h2>
                    <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-bold tracking-wide">
                      {section.attributeType}
                    </span>
                    <span className="rounded-full bg-black/10 px-3 py-1 text-xs">
                      {section.layout.replaceAll('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                    {section.hidden ? <FaEyeSlash /> : <FaEye />}
                    <span>{section.hidden ? 'Hidden' : 'Visible'} · {section.items.length} cards</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {permissions.includes('update') && (
                    <button
                      type="button"
                      className="rounded-full bg-white p-3 shadow-sm transition hover:-translate-y-0.5"
                      aria-label={`Edit ${getSectionDisplayTitle(section)}`}
                      onClick={() => navigate(config.path.content.highlightSectionEdit.replace(':id', section.id))}
                    >
                      <FaPencilAlt />
                    </button>
                  )}
                  {permissions.includes('delete') && (
                    <button
                      type="button"
                      className="rounded-full bg-white p-3 text-red-600 shadow-sm transition hover:-translate-y-0.5"
                      aria-label={`Delete ${getSectionDisplayTitle(section)}`}
                      onClick={() => removeSection(section)}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex gap-3 overflow-hidden border-t border-black/10 bg-white/50 p-4">
                {section.items.slice(0, 8).map(item => (
                  <div key={item.cardId || item.attributeId} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white shadow-sm">
                    <img src={item.image.url} alt="" className="h-full w-full object-contain p-1" />
                  </div>
                ))}
              </div>
              </article>
            )
          })}
        </ReactSortable>
      )}
    </Header>
  )
}

export default HighlightSectionPage
